import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { StackBar } from '../ui';
import { verdictLabel } from '../../utils/formatting';
import { VERDICT_CONFIG } from '../../utils/anomalySignals';
import { inventoryLink, pctOf } from '../../utils/branchView';
import { formatShare } from '../../utils/branchCompare';
import InventoryLink from './InventoryLink';
import { criticalLabel, criticalTip, daysInStockSeverity } from '../../utils/dealershipSettings';
import { useDealershipSettings } from '../../contexts/settingsContext';

// Past the last aging boundary red, past the critical days amber (the
// dealership's settings; 180 / 90 by default).
const daysTone = (d, aging) => ({ crit: 'crit', warn: 'warn' }[daysInStockSeverity(d, aging)] || 'muted');

/**
 * Category mix (P-BR-046): every category of the selection (no cut, D16),
 * by units; the bar is the category's share of the selection, amber when its
 * average age is over the critical days (90 by default); "{n} critical"
 * counts units over the critical days (QA P2-5: named after the setting, so
 * it is not mistaken for the aging tile's last bucket).
 */
export const CATEGORY_FOLD = 10;

/**
 * `reference` (compare mode only): { name, share(category) → % } — the
 * benchmark's share of its units in that category, shown under each row.
 */
export function CategoryMixCard({ categories, reference }) {
    // QA BR-3: a long list folds to the first rows; "Show all" keeps every
    // category one click away (D16: no category is cut).
    const [all, setAll] = useState(false);
    const { aging } = useDealershipSettings();
    if (!categories.length) return null;
    const folds = categories.length > CATEGORY_FOLD;
    const shown = folds && !all ? categories.slice(0, CATEGORY_FOLD) : categories;
    return (
        <section className="v2-card br2-card" aria-labelledby="br2-cat-h">
            <div className="br2-row">
                <h2 id="br2-cat-h">Category mix</h2>
                <span className="br2-spacer" />
                <span className="br2-sub">{categories.length} categories · by units · avg days</span>
            </div>
            <ul className="br2-cats">
                {shown.map((c) => (
                    <li key={c.name} className="br2-catrow" data-testid="cat-row">
                        <span className="br2-catrow-nm" title={c.name}>{c.name}</span>
                        <span className="br2-ctrack" aria-hidden="true">
                            <span className={c.avgDays > aging.criticalDays ? 'is-slow' : ''} style={{ width: `${c.pct}%` }} />
                        </span>
                        <span className="br2-catrow-n">{c.count}</span>
                        <span className={`br2-catrow-d br2-ink-${daysTone(c.avgDays, aging)}`}>{c.avgDays}d avg</span>
                        <span className="br2-catrow-c">
                            {c.critical > 0 && (
                                <span className="v2-chip v2-chip--crit br2-crit" title={criticalTip(aging.criticalDays)}>
                                    {c.critical} critical
                                </span>
                            )}
                        </span>
                        {reference && (
                            <span className="br2-catrow-ref" data-testid="cat-ref" title={reference.title || undefined}>
                                {c.pct}% of units · {reference.name} {formatShare(reference.share(c.name))}
                            </span>
                        )}
                    </li>
                ))}
            </ul>
            {folds && (
                <button type="button" className="v2-clear br2-fold" aria-expanded={all} onClick={() => setAll((v) => !v)}>
                    {all ? 'Show fewer' : `Show all ${categories.length} categories`}
                </button>
            )}
            <p className="br2-cnote br2-cnote--in">
                Bar: share of the selection; amber when the average is over {aging.criticalDays} days.
                {' '}{criticalLabel(aging.criticalDays)}: {criticalTip(aging.criticalDays)}
            </p>
        </section>
    );
}

// `title`: the names behind a count label ("chosen branches"), on hover.
const referenceShape = PropTypes.shape({ name: PropTypes.string.isRequired, share: PropTypes.func.isRequired, title: PropTypes.string });

CategoryMixCard.propTypes = { categories: PropTypes.arrayOf(PropTypes.object).isRequired, reference: referenceShape };

const VERDICT_TONE = { critical: 'crit', warning: 'warn', positive: 'pos' };

/**
 * Anomaly signals (P-BR-048): units per verdict in today's order, today's
 * labels ("Above Estimate"). Each chip opens the selection in the inventory
 * with that signal (D14). Hidden when the selection has none.
 */
export function AnomalySignalsCard({ verdicts, linkBase, reference }) {
    if (!verdicts.length) return null;
    return (
        <section className="v2-card br2-card" aria-labelledby="br2-sig-h">
            <h2 id="br2-sig-h">Anomaly signals</h2>
            <div className="br2-sigs">
                {verdicts.map((v) => {
                    const tone = VERDICT_TONE[VERDICT_CONFIG[v.verdict]?.severity] || 'warn';
                    const chip = (
                        <InventoryLink
                            key={v.verdict}
                            to={inventoryLink({ ...linkBase, params: { signal: v.verdict } })}
                            className={`br2-chip-t br2-chip-t--${tone} is-on`}
                            data-verdict={v.verdict}
                        >
                            <i className={`v2-tone-${tone === 'crit' ? 'red' : tone === 'warn' ? 'amber' : 'sage'}`} aria-hidden="true" />
                            {verdictLabel(v.verdict)}
                            <span className="br2-chip-c">{v.count}</span>
                        </InventoryLink>
                    );
                    if (!reference) return chip;
                    return (
                        <span key={v.verdict} className="br2-sigw">
                            {chip}
                            <span className="br2-sigref" data-testid="sig-ref" title={reference.title || undefined}>
                                {formatShare(reference.total > 0 ? (v.count / reference.total) * 100 : null)} · {reference.name} {formatShare(reference.share(v.verdict))}
                            </span>
                        </span>
                    );
                })}
            </div>
            <p className="br2-cnote br2-cnote--in">Each chip opens the inventory filtered to this selection and signal.</p>
        </section>
    );
}

AnomalySignalsCard.propTypes = {
    verdicts: PropTypes.arrayOf(PropTypes.shape({ verdict: PropTypes.string, count: PropTypes.number })).isRequired,
    linkBase: PropTypes.object.isRequired,
    // { name, total (selection units), share(verdict) → % } in compare mode.
    reference: PropTypes.shape({
        name: PropTypes.string, total: PropTypes.number, share: PropTypes.func, title: PropTypes.string,
    }),
};

export const SPEED_BUCKETS = [
    { key: 'fast', label: 'Fast <45d', tone: 'sage' },
    { key: 'normal', label: 'Normal 45–90d', tone: 'slate' },
    { key: 'slow', label: 'Slow 90–180d', tone: 'amber' },
    { key: 'verySlow', label: 'Very Slow 180+d', tone: 'red' },
];

/**
 * AI sell speed (P-BR-049): units by predicted days to sell; the % is of
 * every selected unit (D16). Hidden when no unit has a prediction.
 */
export function SellSpeedCard({ speed, reference }) {
    if (!speed) return null;
    return (
        <section className="v2-card br2-card" aria-labelledby="br2-spd-h">
            <h2 id="br2-spd-h">AI sell speed</h2>
            <StackBar segments={SPEED_BUCKETS.map((s) => ({ key: s.key, tone: s.tone, value: speed[s.key] }))} />
            <div className="br2-spd">
                {SPEED_BUCKETS.map((s) => (
                    <div key={s.key} data-speed={s.key}>
                        <span className="br2-spd-n">{speed[s.key]}</span>
                        <span className="br2-spd-l"><i className={`v2-tone-${s.tone}`} aria-hidden="true" />{s.label}</span>
                        <span className="br2-spd-p">{pctOf(speed[s.key], speed.total)}%</span>
                        {reference && (
                            <span className="br2-spd-r" data-testid="spd-ref" title={reference.title || undefined}>{reference.name} {formatShare(reference.share(s.key))}</span>
                        )}
                    </div>
                ))}
            </div>
            <p className="br2-cnote br2-cnote--in">
                {speed.predicted.toLocaleString('en-US')} of {speed.total.toLocaleString('en-US')} units have a prediction; shares are of all units.
            </p>
        </section>
    );
}

SellSpeedCard.propTypes = { speed: PropTypes.object, reference: referenceShape };
